const SHOW_MS = 2600;
const RETRY_MS = 2000;
const EXIT_MS = 280;
const TAP_PX = 6;
const DISMISS_PX = 24;
const FLICK_PX = 10;
const FLICK_MS = 400;

export function createCartNotice({ root, delay, now, requestFrame, track, cart }) {
  const drawer = root.getElementById('cart-drawer');
  const banner = drawer && drawer.querySelector('[data-cart-notice]');
  if (!drawer || !banner) return null;

  const image = banner.querySelector('[data-notice-image]');
  const title = banner.querySelector('[data-notice-title]');
  const subtitle = banner.querySelector('[data-notice-subtitle]');

  let cancelTimer = null;
  let drag = null;

  const stopTimer = () => {
    if (cancelTimer) cancelTimer();
    cancelTimer = null;
  };

  const startTimer = (ms) => {
    stopTimer();
    cancelTimer = delay(dismiss, ms);
  };

  function imageForGroup(group) {
    if (!group) return '';
    const line = Array.from(drawer.querySelectorAll('.cart-item')).find((node) => node.dataset.group === group);
    const node = line && line.querySelector('.cart-item__image');
    if (!node) return '';
    const img = node.tagName === 'IMG' ? node : node.querySelector('img');
    return (img && img.getAttribute('src')) || '';
  }

  function settle() {
    banner.classList.remove('is-dragging');
    banner.style.transform = '';
  }

  function dismiss() {
    stopTimer();
    drag = null;
    settle();
    banner.classList.remove('is-visible');
    cancelTimer = delay(() => { banner.hidden = true; }, EXIT_MS);
  }

  function show({ title: titleText, subtitle: subtitleText, image: imageSrc, group } = {}) {
    if (!titleText) return;
    stopTimer();
    drag = null;
    title.textContent = titleText;
    subtitle.textContent = subtitleText || '';
    const src = imageSrc || imageForGroup(group);
    if (image) {
      if (src) image.setAttribute('src', src);
      else image.removeAttribute('src');
      image.hidden = !src;
    }
    banner.dataset.group = group || '';
    const header = drawer.querySelector('.cart-drawer__header');
    banner.style.setProperty('--notice-top', `${(header ? header.offsetHeight : 0) + 8}px`);
    settle();
    banner.hidden = false;
    requestFrame(() => requestFrame(() => {
      if (!banner.hidden) banner.classList.add('is-visible');
    }));
    startTimer(SHOW_MS);
  }

  function onPointerDown(event) {
    if (!banner.classList.contains('is-visible')) return;
    drag = { y0: event.clientY, dy: 0, t0: now() };
    stopTimer();
    banner.classList.add('is-dragging');
    try { banner.setPointerCapture(event.pointerId); } catch {}
  }

  function onPointerMove(event) {
    if (!drag) return;
    drag.dy = event.clientY - drag.y0;
    banner.style.transform = `translateY(${Math.min(0, drag.dy)}px)`;
  }

  function onPointerEnd() {
    if (!drag) return;
    const { dy, t0 } = drag;
    drag = null;
    const quick = now() - t0 < FLICK_MS;
    if (Math.abs(dy) < TAP_PX) {
      tap();
      return;
    }
    if (dy < -DISMISS_PX || (quick && dy < -FLICK_PX)) {
      dismiss();
      return;
    }
    settle();
    startTimer(RETRY_MS);
  }

  function onPointerCancel() {
    if (!drag) return;
    drag = null;
    settle();
    startTimer(RETRY_MS);
  }

  function tap() {
    const group = banner.dataset.group;
    if (group && cart && typeof cart.openLineDetails === 'function') cart.openLineDetails(group);
    track('cart_upsell_toast_tap', { group: group || '' });
    dismiss();
  }

  banner.addEventListener('pointerdown', onPointerDown);
  banner.addEventListener('pointermove', onPointerMove);
  banner.addEventListener('pointerup', onPointerEnd);
  banner.addEventListener('pointercancel', onPointerCancel);
  banner.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    tap();
  });

  return { show, dismiss };
}

if (typeof document !== 'undefined') {
  const start = () => {
    const notice = createCartNotice({
      root: document,
      delay: (callback, ms) => {
        const id = setTimeout(callback, ms);
        return () => clearTimeout(id);
      },
      now: () => performance.now(),
      requestFrame: (callback) => requestAnimationFrame(callback),
      track: (event, payload) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event, ...payload });
      },
      cart: window.fnCart,
    });
    if (!notice) return;
    window.fnCartNotice = notice;
    document.dispatchEvent(new CustomEvent('fn:cart-notice-ready'));
  };
  if (window.fnCart) start();
  else document.addEventListener('fn:cart-ready', start, { once: true });
}
